import React, { useState, useEffect } from 'react';
import FileSelector from '../../common/FileSelector.jsx';
import Select from '../../common/Select.jsx';
import { Col, Row } from '../../bootstrap/index.jsx';
import ToggleSwitch from '../../common/ToggleSwitch.jsx';
import { readExcelSheetNames } from './readExcel.js';
import LoadingIndicator from '../../common/loading_indicator.jsx';
import {getInitialColumnDefs} from './settings.js';
import excelParserWorker from '@workers/excelParserWorker.js?worker';
import { DEFAULT_SETTINGS_STATE } from './ImportWizardModal.jsx';
import { flattenMostFrequent } from './helpers.js';
import { DEFAULT_XLSX_WORK_BOOK } from './useSteps.jsx';
import _ from 'lodash';

function removeInfoCols(obj) {
  if (_.isPlainObject(obj)) {
    delete obj.excel_column;
    delete obj.ignore_blanks;
    for (const key in obj) {
      removeInfoCols(obj[key]); // recurse
    }
  } else if (_.isArray(obj)) {
    obj.forEach(removeInfoCols);
  }
  return obj;
}
const MAX_ROW_INDEX_HEADER = 20;
const FILE_SIZE_LIMIT = 50000000 // 50MB
const TOTAL_MAX_CELLS = 9000000 // 9.1 mil is processable, 9.6mil fail to load completely.

export default function UploadExcelFile({
    gridRef,
    datagrid,
    xlsWorkBook,
    setXlsWorkBook,
    settings,
    updateSettings,
    processingExcel,
    setProcessingExcel,
    setEnableControls,
    fileSizeError,
    setFileSizeError,
    toggleGrid,
    wizardOptions = {},
  }){
  const gridApi = gridRef.current?.api;
  const [processedRowCount, setProcessedRowCount] = useState(0);
  // Because getting displayedRow cause wrong indexing, need to go through all row node and record the row can be selected for header.
  const [maxHeaderNodes, setMaxHeaderNodes] = useState([]);

  const {file} = xlsWorkBook;

  const handleFileChange = (file) => {
    toggleGrid('excel')
    setXlsWorkBook((v) => ({
      ...DEFAULT_XLSX_WORK_BOOK,
      file: file,
    }));
    setMaxHeaderNodes([]);
    setProcessingExcel(true);
    readExcelSheetNames(file).then(ws_names => {
      setXlsWorkBook((v) => ({...DEFAULT_XLSX_WORK_BOOK, file: v.file, ws_names: ws_names}));
      setProcessingExcel(false);
    });
    updateSettings({
      ...DEFAULT_SETTINGS_STATE,
      columns: removeInfoCols(settings.columns || {})
    })
    gridApi.setGridOption('columnDefs', []);
    gridApi.setGridOption('rowData', []);
    if (file.size > FILE_SIZE_LIMIT){
      setFileSizeError(true);
    } else {
      setFileSizeError(false);
    }
  };

  const handleWorksheetChange = (ws_name) => {
    var columns = settings.columns;

    // Some sort of reset, figure out why it's here?
    // Should it be unified at top level?
    datagrid.columns.forEach((c) => {
      if (columns[c.accessor]) {
        columns[c.accessor]['excel_column'] = undefined;
        if (c.is_selection) columns[c.accessor]['default_value'] = undefined;
      }
    });
    updateSettings({ws_name: ws_name, columns: columns});
    setProcessingExcel(true);

    // Create a new Web Worker
    let timeStart = new Date()
    const worker = excelParserWorker({ name: 'excel-parser-worker' });

    let totalRowCount = 0;
    let totalColCount = 0
    worker.onmessage = (e) => {
      const { type } = e.data;

      if (type === 'columns') {
        const colNames = e.data.col_names;
        totalColCount = (colNames || []).length;
        const columnDefs = getInitialColumnDefs(datagrid, settings, colNames);
        gridApi.setGridOption('columnDefs', columnDefs);

        // Reset row count
        totalRowCount = 0;

        // Update workbook state with column names
        setXlsWorkBook((v) => ({
          ...v,
          ws_column_names: colNames,
        }));
      }
      if ( type === 'rowCount') {
        // Rows in excel including blanks
        setXlsWorkBook((v) => ({
          ...v,
          total_row_count: e.data.rowCount,
        }));
        if (totalColCount * e.data.rowCount > TOTAL_MAX_CELLS){
          worker.terminate();
          setProcessingExcel(false);
        }
      }
      else if (type === 'columnFormats'){
        const colFormats = flattenMostFrequent(e.data.colFormats)

        setXlsWorkBook((v) => ({
          ...v,
          resolved_column_types: colFormats,
        }));
      }
      else if (type === 'rows') {
        const rows = e.data.rows;
        totalRowCount += rows.length;
        // Add rows incrementally
        setProcessedRowCount(prev => prev + rows.length);
        gridApi.applyTransactionAsync({ add: rows });
      }

      else if (type === 'done') {
        setProcessingExcel(false);
        setEnableControls(true);
        // Parsed rows, skipping blanks
        setXlsWorkBook((v) => ({
          ...v,
          total_row_count: totalRowCount,
        }));
        worker.terminate();
        console.log(`Took ${new Date() - timeStart}ms`);
      }

      else if (type === 'error') {
        console.error('Worker error:', e.data.error);
        // TODO: Proper error display
        setProcessingExcel(false);
        worker.terminate();
      }
    };

    // Send the file to the web worker for processing
    worker.postMessage({ file, ws_name });
  };

  const handleHeaderIdxChange = (val) => {
    let col_names = val > -1 ?
      xlsWorkBook.ws_column_names.map((v, i) => ({...v, label: `${v.col_name}: ${getRowColumnValue(val || 0, i)}`})) :
      xlsWorkBook.ws_column_names.map((v, i) => ({...v, label: v.col_name}));

    setXlsWorkBook((v) => ({...v, ws_column_names: col_names}));
    let excelColIndex = 0;

    // Add the names from header row to colDef headerNames
    let columnDefs = gridApi.getGridOption('columnDefs').map(colDef => {
      if (!colDef.field.includes('-e')) return colDef;
      colDef.headerName = col_names[excelColIndex].label;
      excelColIndex += 1;
      return colDef;
    });
    gridApi.setGridOption('columnDefs', columnDefs);

    updateSettings(({ws_header_idx: val, range_from: val + 2, range_to: xlsWorkBook.total_row_count}))
  };

  const handleRangeFromChange = (e) => {
    updateSettings(({range_from: parseRangeValue(e)}))
  };

  const handleRangeToChange = (e) => {
    updateSettings(({range_to: parseRangeValue(e)}))
  };

  const parseRangeValue = (e) => {
    let parsedValue = parseInt(e.target.value);
    if (parsedValue > xlsWorkBook.total_row_count)
      parsedValue = xlsWorkBook.total_row_count;
    else if (parsedValue < 0)
      parsedValue = 0;
    return isNaN(parsedValue) ? '' : parsedValue
  };

  const getRowColumnValue = (rowIndex, columnIndex) => {
    let columnValue = '';
    if (gridApi) {
      let nodes = maxHeaderNodes;
      if (nodes.length === 0){
        let index = 0
        gridApi.forEachNode(node => {
          if (index < MAX_ROW_INDEX_HEADER){
            nodes.push(node)
          }
        });
        setMaxHeaderNodes(nodes);
      }
      let rowNode = nodes[rowIndex]
      if (rowNode) {
        columnValue = rowNode.data[columnIndex];
      }
    }
    return columnValue == undefined ? '' : columnValue;
  };


  useEffect(() => {
    gridApi?.onFilterChanged();
  }, [settings.ws_name, settings.all_rows, settings.range_from, settings.range_to]);

  useEffect(() => {
    if ((xlsWorkBook.total_row_count || 0) * (xlsWorkBook.ws_column_names || []).length > TOTAL_MAX_CELLS){
      setFileSizeError(true);
    }
  }, [xlsWorkBook.total_row_count, xlsWorkBook.ws_column_names])

  let processingMessage = I18n.t('excel_import_processing_message_2') + ' (' + processedRowCount + '/' + xlsWorkBook.total_row_count + ')';
  if (!xlsWorkBook.total_row_count || xlsWorkBook.total_row_count === 0) processingMessage = I18n.t('excel_import_processing_message_1');

  return (<>
    <div className='ml-1 pt-1'>
      <Row>
        <Col size='xs-12'>
          {xlsWorkBook.ws['!merges'] && <div className='alert alert-info'>{I18n.t('excel_contains_merged_columns_notice')}</div>}
          {fileSizeError && <div className='alert alert-info'>{I18n.t('excel_import_file_size_error', {size: FILE_SIZE_LIMIT/1000000, cell_lim: TOTAL_MAX_CELLS/1000000})}</div>}

          <div className='form-group'>
            <label className='w-100'>{I18n.t('import_file', { scope: 'di' })}</label>
            <FileSelector accept='.xlsx' defaultFilename={file?.name} onChange={handleFileChange} confirmationText={I18n.t('excel_import_switch_file_confirm')} />
          </div>

          {file && !processingExcel && !fileSizeError && <WorksheetSelection   
            value={settings.ws_name}
            onChange={handleWorksheetChange}
            options={xlsWorkBook.ws_names.map((v) => ({ value: v, label: v }))}
          />}

          {processingExcel && <LoadingIndicator size='sm' additionalInfo={processingMessage}/> }

          {file && settings.ws_name && !processingExcel && !fileSizeError && <RowSelections 
            rowCount={xlsWorkBook.total_row_count}
            settings={settings}
            updateSettings={updateSettings}
            handleHeaderIdxChange={handleHeaderIdxChange}
            handleRangeFromChange={handleRangeFromChange}
            handleRangeToChange={handleRangeToChange}
            wizardOptions={wizardOptions}
          />}
        </Col>
      </Row>
    </div>
  </>)
};

function WorksheetSelection(props){ return <div className='form-group'>
    <label className='w-100'>{I18n.t('worksheet')}</label>
    <Select
      {...props}
    />
  </div>
}

function RowSelections({settings,
  updateSettings,
  rowCount,
  handleRangeFromChange,
  handleRangeToChange,
  handleHeaderIdxChange,
  wizardOptions = {},
}){
  return <>
    <div className='form-group'>
      <label className='w-100'>{I18n.t('select_header_row', { scope: 'di' })}</label>
      <Select
        value={settings.ws_header_idx}
        onChange={handleHeaderIdxChange}
        options={[
          { label: I18n.t('none'), value: -1 },
          // Limit select options to first 20 rows, as select breaks on large excels if all rows as options
          // Should possibly show a number input instead with large excels
          ...(new Array(Math.min(rowCount, MAX_ROW_INDEX_HEADER)).fill(0).map((d, i) => ({ value: i, label: i + 1 })))
        ]}
      />
    </div>

    {wizardOptions.showBodyRowSelector &&
      <div className='form-group'>
        <label className='w-100'>{I18n.t('select_body_rows')}</label>
        <div className='d-flex'>
          <div className='d-flex align-items-center mr-1'>
            <ToggleSwitch
              size='sm'
              defaultIsOn={true}
              isOn={settings.all_rows}
              handleToggle={() => updateSettings(({all_rows: !settings.all_rows}))}
              wrapperProps={{style: {marginRight: '0.5rem'}}}
            />
            <label>{I18n.t('all_rows')}</label>
          </div>

          <div className='d-flex align-items-center col-xs-12'>
            <label>{I18n.t('rows')}</label>
            <input
              type='number'
              className='form-control'
              style={{height: '25px', margin: '0 5px 5px 5px'}}
              value={settings.range_from}
              min={settings.ws_header_idx + 1}
              max={rowCount}
              disabled={settings.all_rows}
              onChange={handleRangeFromChange}
            />

            <label>{I18n.t('to')}</label>
            <input
              type='number'
              className='form-control'
              style={{height: '25px', margin: '0 5px 5px 5px'}}
              value={settings.range_to}
              min={settings.range_from}
              max={rowCount}
              disabled={settings.all_rows}
              onChange={handleRangeToChange}
            />
          </div>
        </div>
      </div>
    }
  </>
}
